import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {observer} from 'mobx-react';
import {EPLines} from './ep-lines';
import {EP_SHORT_PLATE_SIDE_MARGIN_IN, EPMethod, isEPCustomLike, PCS_CSS, ProductCustomizationState, renderSettings} from '../../../rendering/render.def';
import {applyEpInkCentering, EP_INHERITED_LINE_HEIGHT, sanitizeEPContent, transformPtToCqh} from '../ep-content-utils';
import {applyFontFamiliesFromDataFont} from './engraved-plate-extended.store';

type Props = {
    css: PCS_CSS;
    pcs: ProductCustomizationState;
};

export const EPContent: React.FC<Props> = observer(({pcs, css}: Props) => {
    const isEPCustom = isEPCustomLike(pcs.EPMethodID);
    const isEPSingleDoc = pcs.EPMethodID === EPMethod.SingleDoc;
    const isEPTemplate = pcs.EPMethodID === EPMethod.Template;
    const showEditor = isEPCustom || isEPSingleDoc || isEPTemplate;
    const plateHeightPt = css.ep_plateHeightPt || Math.round((pcs.EPHeight || 1) * 72);

    const sanitizedContent = useMemo(() => transformPtToCqh(applyFontFamiliesFromDataFont(sanitizeEPContent(pcs.EPContent || ''))), [pcs.EPContent]);

    // Optical ink centering (see applyEpInkCentering): non-template methods only —
    // Custom/Admin (3/6) and SingleDoc (4); Template keeps the plain box stack.
    const inkCenterEnabled = isEPCustom || isEPSingleDoc;
    const inkBlockRef = useRef<HTMLDivElement>(null);
    const [, setFontTick] = useState(0);

    useEffect(() => {
        // Canvas metrics measured before a webfont finishes loading come from the
        // fallback face — bump state so the measuring effect re-runs when loads settle.
        const bump = () => setFontTick(t => t + 1);
        document.fonts?.ready?.then(bump);
        document.fonts?.addEventListener?.('loadingdone', bump);
        return () => document.fonts?.removeEventListener?.('loadingdone', bump);
    }, []);

    useLayoutEffect(() => {
        if (inkBlockRef.current) applyEpInkCentering(inkBlockRef.current, inkCenterEnabled);
    });

    const epStyle: React.CSSProperties = {
        backgroundImage: css.ep_backgroundImage,
        backgroundSize: css.ep_backgroundSize,
        color: css.ep_color,
        left: css.ep_left,
        top: css.ep_top,
        width: css.ep_width,
        height: css.ep_height,
        boxSizing: 'border-box',
        textAlign: showEditor ? 'initial' : 'center',
        wordBreak: showEditor ? 'break-word' : undefined,
        overflow: 'hidden',
        // set here, not inherited from the page: the image generator's page has no Bootstrap body 1.5
        lineHeight: EP_INHERITED_LINE_HEIGHT,
        ['--ep-h-pt' as string]: plateHeightPt,
    } as React.CSSProperties;

    // Custom (Quill) content is laid out in the SAME reference box the editor uses
    // (plate inches x referenceDPI, fractional, with the same padding rule) and then
    // CSS-scaled to the actual rendered plate size. Scaling a laid-out box preserves
    // line breaks exactly, so the frame preview can never wrap differently from the
    // editor — device-DPI rounding or padding drift would otherwise flip borderline
    // lines (single in the editor, double on the frame, or vice versa).
    const plateWidthPt = css.ep_plateWidthPt || Math.round((pcs.EPWidth || 3) * 72);
    const refW = (plateWidthPt / 72) * renderSettings.referenceDPI;
    const refH = (plateHeightPt / 72) * renderSettings.referenceDPI;
    const isShortPlate = plateHeightPt < 72;
    // Same padding the editor applies: 0.25" sides only on short plates; the standard
    // 7.5px reference padding on all four sides of taller plates.
    const refPadding = isShortPlate ? `0 ${EP_SHORT_PLATE_SIDE_MARGIN_IN * renderSettings.referenceDPI}px` : `${renderSettings.topPadding}px ${renderSettings.defaultPadding}px ${renderSettings.defaultPadding}px ${renderSettings.defaultPadding}px`;
    const actualW = parseFloat(css.ep_width) || refW;
    const actualH = parseFloat(css.ep_height) || refH;

    // Two layers, exactly like the editor: the OUTER div is the size container and
    // must stay unpadded (cqh resolves against the container's CONTENT box, so
    // padding here would shrink the font-resolution height — the editor keeps
    // .ql-container unpadded for the same reason); the INNER div carries the padding
    // and lays the text out.
    //
    // The scale is UNIFORM (width-derived) and the box is re-centered vertically:
    // the device box can be a different aspect than the plate (the image generator
    // adds the same absolute epBorderAdjust to both axes of InnerAccent plates, and
    // device-DPI flooring nudges each axis independently), and scaling each axis
    // separately would visibly stretch the glyphs.
    const scale = actualW / refW;
    const refBoxStyle: React.CSSProperties = {
        position: 'absolute',
        top: (actualH - refH * scale) / 2,
        left: 0,
        width: refW,
        height: refH,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        containerType: 'size',
        ['--ep-h-pt' as string]: plateHeightPt,
        overflow: 'hidden',
    } as React.CSSProperties;

    const refContentStyle: React.CSSProperties = {
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        padding: refPadding,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        // Quill's .ql-editor lays text out with pre-wrap + break-word; match it so
        // preserved spaces and long words wrap identically.
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word',
    };

    const innerPadStyle: React.CSSProperties = {
        padding: showEditor ? '0' : css.ep_padding || '0',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        position: 'relative',
    };

    return (
        <div
            id="ep"
            className="engrave-plate-frame"
            style={epStyle}
        >
            {isEPCustom && showEditor ? (
                <div style={refBoxStyle}>
                    <div style={refContentStyle}>
                        {/* single flex child so ink centering can translate the whole
                            block; block layout inside is identical to the bare <p>s.
                            position/width are explicit because product.scss makes every
                            `#frame-container > div div` absolute (shrink-to-fit). */}
                        <div
                            ref={inkBlockRef}
                            style={{position: 'relative', width: '100%'}}
                            dangerouslySetInnerHTML={{__html: sanitizedContent}}
                        />
                    </div>
                </div>
            ) : (
                <div style={innerPadStyle}>
                    {showEditor ? (
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                position: 'relative',
                            }}
                        >
                            <div
                                ref={inkBlockRef}
                                style={{position: 'relative', width: '100%'}}
                                dangerouslySetInnerHTML={{__html: sanitizedContent}}
                            />
                        </div>
                    ) : (
                        <EPLines
                            pcs={pcs}
                            css={css}
                        />
                    )}
                </div>
            )}
        </div>
    );
});
